import React, { useEffect, useState } from "react";
import { Label, Row, Col, Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { FiTrash2 } from "react-icons/fi";
import styles from "./WholesalerVariationForm.module.scss";
import MultiSelect from "../../common/MultiSelect/MultiSelect";
import ColorPicker from "../../common/ColorPicker/ColorPicker";
import ConfirmationModal from "../../common/ConfirmationModal/ConfirmationModal";
import { FaCheckCircle } from "react-icons/fa";
import WholesalerVariationTable from "../WholesalerVariationTable/WholesalerVariationTable";

const variationOptions = [
    { label: "Color", type: "color" },
    { label: "Size", type: "size" },
    { label: "Length", type: "length" },
    { label: "No Variations", type: "no-variations" },
];
const sizeOptions = ["Small", "Medium", "Large"];
const lengthOptions = ["10 cm", "15 cm", "20 cm"];

const WholesalerVariationForm = () => {
    const [selectedVariations, setSelectedVariations] = useState({});
    const [combinations, setCombinations] = useState([]);
    const [colors, setColors] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalAction, setModalAction] = useState(null);
    const [modalContent, setModalContent] = useState({
        title: '',
        body: '',
        confirmText: 'Yes',
        cancelText: 'No',
    });
    const toggleModal = () => setModalOpen(!modalOpen);

    const [selectedVariationTypes, setSelectedVariationTypes] = useState([]);

    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

    const handleConfirmAction = () => {
        if (modalAction) {
            modalAction();
        }
        toggleModal();
    };

    const handleAddVariation = (option) => {
        // Prevent unselecting an already selected option
        if (selectedVariationTypes.includes(option)) {
            return; // Do nothing if the option is already selected
        } else {
            setSelectedVariationTypes([...selectedVariationTypes, option]);
        }

        const selectedType = option;

        // Handle "no-variations" case
        if (selectedType === "no-variations") {
            if (Object.keys(selectedVariations).length === 0) {
                setModalContent({
                    title: "Confirm Action",
                    body: "You have not selected any variations yet. Do you want to continue without variations?",
                    confirmText: "Yes, Proceed",
                    cancelText: "Cancel",
                });
            } else {
                setModalContent({
                    title: "Confirm Action",
                    body: "Selecting 'No Variations' will remove all variation types and combinations. Do you want to proceed?",
                    confirmText: "Yes, Remove Variations",
                    cancelText: "Cancel",
                });
            }

            setModalAction(() => () => {
                setSelectedVariations({ "no-variations": [] });
                setSelectedVariationTypes(["no-variations"]);
                setCombinations([]);
            });

            toggleModal();
        } else {
            const updatedVariationTypes = selectedVariationTypes.filter(
                (variation) => variation !== "no-variations"
            );

            if (!updatedVariationTypes.includes(option)) {
                setSelectedVariationTypes([...updatedVariationTypes, option]);
            }

            // Remove "no-variations" if it exists in selected variations.
            const updatedVariations = { ...selectedVariations };
            delete updatedVariations["no-variations"];

            // If the selected option is not already in variations, add it.
            if (!selectedVariations[selectedType]) {
                setSelectedVariations({ ...updatedVariations, [selectedType]: [] });
            }
        }
    };

    const handleVariationChange = (type, value) => {
        const updatedVariations = { ...selectedVariations };

        // If value is empty, remove the variation
        if (!value || value.length === 0) {
            delete updatedVariations[type];
            if (type === "color") {
                setColors([]);
            }
            const updatedOptions = selectedVariationTypes.filter((item) => item !== type);
            setSelectedVariationTypes(updatedOptions);
        } else {
            // Add or update the variation (including colors)
            updatedVariations[type] = value;
        }

        setSelectedVariations(updatedVariations);

        if (Object.keys(updatedVariations).length === 0) {
            setCombinations([]);
        } else {
            handleGenerateCombinations(updatedVariations);
        }
    };

    const handleRemoveVariation = (type) => {
        setModalAction(() => () => handleVariationChange(type, []));
        setModalContent({
            title: 'Confirm Deletion',
            body: `Are you sure you want to delete the "${type}" variation? This will remove all its combinations.`,
            confirmText: 'Yes, Delete',
            cancelText: 'Cancel',
        });

        toggleModal();
    };

    const handleGenerateCombinations = (variations = selectedVariations) => {
        const keys = Object.keys(variations).filter(
            (key) => key !== "no-variations"
        );
        const values = Object.values(variations).filter((arr) => arr.length > 0);

        if (keys.length === 0 || values.length === 0) {
            return;
        }

        const generateCartesianProduct = (arrays) => {
            return arrays.reduce(
                (acc, curr) =>
                    acc
                        .map((x) => curr.map((y) => [...x, y]))
                        .reduce((a, b) => a.concat(b), []),
                [[]]
            );
        };

        const generatedCombinations = generateCartesianProduct(values).map(
            (combo) =>
                combo.reduce((acc, curr, idx) => {
                    acc[keys[idx]] = curr;
                    return acc;
                }, {})
        );

        setCombinations(generatedCombinations);
    };

    const handleColorChange = (updatedColors) => {
        const colorNames = updatedColors.map(color => color.name);
        setColors(updatedColors);
        handleVariationChange("color", colorNames);
    };

    useEffect(() => {
        handleGenerateCombinations();
    }, [selectedVariations]);

    return (
        <div className={styles.variationContainer}>
            <div>
                <Row className={styles.formGroup}>
                    <Col lg={"auto"}>
                        <Label htmlFor="variationType">
                            Choose Variation Type:
                        </Label>
                    </Col>
                    <Col lg={5} xl={4}>
                        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className={styles.multiSelectDropdown}>
                            <DropdownToggle className={styles.multiSelectToggle} caret>
                                Select Variation Type
                            </DropdownToggle>
                            <DropdownMenu className={styles.multiSelectMenu}>
                                {variationOptions.map((option) => {
                                    const isActive = selectedVariationTypes.includes(option.type);
                                    return (
                                        <DropdownItem
                                            key={option.type}
                                            onClick={() => handleAddVariation(option.type)}
                                            active={isActive}
                                            className={styles.multiSelectMenuOption}
                                            style={{
                                                backgroundColor: isActive ? "#41B8E4" : "transparent",
                                                color: isActive ? "#ffffff" : "#1A2425",
                                            }}
                                        >
                                            {option.label}
                                            {isActive && <FaCheckCircle className={styles.checkIcon} />}
                                        </DropdownItem>
                                    );
                                })}
                            </DropdownMenu>
                        </Dropdown>
                    </Col>
                </Row>

                {/* Selected Variation Rows */}
                {Object.keys(selectedVariations).map((type, index) => (
                    <Row key={index} className={styles.variationRow}>
                        <Col xs={10} lg={11}>
                            {type === "color" && (
                                <ColorPicker
                                    onColorsChange={handleColorChange}
                                    colors={colors}
                                    toggleModal={toggleModal}
                                    setModalContent={setModalContent}
                                    setModalAction={setModalAction}
                                />
                            )}

                            {type === "size" && (
                                <MultiSelect
                                    options={sizeOptions}
                                    label="Select Sizes"
                                    selected={selectedVariations["size"] || []}
                                    onChange={(selected) => handleVariationChange("size", selected)}
                                    toggleModal={toggleModal}
                                    setModalContent={setModalContent}
                                    setModalAction={setModalAction}
                                />
                            )}

                            {type === "length" && (
                                <MultiSelect
                                    options={lengthOptions}
                                    label="Select Lengths"
                                    selected={selectedVariations["length"] || []}
                                    onChange={(selected) =>
                                        handleVariationChange("length", selected)
                                    }
                                    toggleModal={toggleModal}
                                    setModalContent={setModalContent}
                                    setModalAction={setModalAction}
                                />
                            )}

                            {type === "no-variations" && (
                                <div className={styles.noVariations}>No Variations</div>
                            )}
                        </Col>

                        <Col xs={2} lg={1} className="text-end ps-2">
                            {type != "no-variations" && (
                                <Button
                                    color="danger"
                                    onClick={() => handleRemoveVariation(type)}
                                    className={styles.removeButton}
                                >
                                    <FiTrash2 size={18} />
                                </Button>
                            )}
                        </Col>
                    </Row>
                ))}
            </div>

            {/* Combinations Section */}
            <WholesalerVariationTable combinations={combinations} />

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={modalOpen}
                toggle={toggleModal}
                title={modalContent.title}
                body={modalContent.body}
                confirmText={modalContent.confirmText}
                cancelText={modalContent.cancelText}
                onConfirm={handleConfirmAction}
                onCancel={toggleModal}
            />

        </div>
    );
};

export default WholesalerVariationForm;